/**
 * 爆款模板 — 表单类型定义与工具函数
 */
import type { TemplateDetailVO } from '@/3.0/ai-viral-template/_components/template-ui/types';

// --- 类型 ---

export interface ViralTemplateFormFieldOption {
  value: string;
  label: string;
}

export type ViralTemplateFormFieldType = string;

export interface ViralTemplateFormField {
  key: string;
  label: string;
  type: ViralTemplateFormFieldType;
  required: boolean;
  category?: string;
  placeholder?: string;
  options?: ViralTemplateFormFieldOption[];
  default?: string;
  maxLength?: number;
}

export interface ViralTemplateFormSchema {
  fields?: Record<string, unknown>[];
}

export interface ViralTemplateFormData {
  templateId?: number;
  name?: string;
  cover?: string;
  preview?: string;
  credit?: number;
  credit_cost?: number;
  formSchema?: ViralTemplateFormSchema;
  fields?: ViralTemplateFormField[];
}

export interface ViralTemplateFormApiResponse {
  success?: boolean;
  code?: number;
  message?: string;
  data?: ViralTemplateFormData;
  [key: string]: any;
}

export interface ViralTemplateTaskSubmitBody {
  id: number;
  formData?: Record<string, unknown>;
}

// --- 工具函数 ---

type TemplateFormField = TemplateDetailVO['formSchema']['fields'][number];

const DEFAULT_VIRAL_TEMPLATE_INPUT_MAX_LENGTH = 100;
const DEFAULT_VIRAL_TEMPLATE_TEXTAREA_MAX_LENGTH = 500;

function normalizeTypeKey(type: unknown): string {
  return String(type ?? '')
    .toLowerCase()
    .replace(/_/g, '');
}

function normString(v: unknown): string {
  return v == null ? '' : String(v);
}

function pickNonEmptyString(...candidates: unknown[]): string {
  for (const candidate of candidates) {
    const s = normString(candidate).trim();
    if (s) return s;
  }
  return '';
}

function normalizeTextSubmitValue(value: unknown): string {
  return String(value ?? '')
    .replace(/\\r\\n|\\n|\\r|\\t/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function coerceDefaultImageUrl(rawDefault: unknown): string {
  if (Array.isArray(rawDefault)) {
    const first = rawDefault.find((x) => typeof x === 'string' && x.length > 0);
    return first != null ? String(first) : '';
  }
  return normString(rawDefault);
}

/** 将接口 / mock 中的单条 field 规范为 TemplateDetailVO.formSchema.fields 元素 */
export function normalizeTemplateFormField(
  raw: Record<string, unknown>,
): TemplateFormField | null {
  const typeKey = normalizeTypeKey(raw.type);
  const key = normString(raw.key) || 'field';
  const label = normString(raw.label) || key;
  const required = Boolean(raw.required);

  if (typeKey === 'input') {
    return {
      key,
      label,
      type: 'input',
      required,
      placeholder: normString(raw.placeholder),
      maxLength: Number(raw.maxLength ?? DEFAULT_VIRAL_TEMPLATE_INPUT_MAX_LENGTH),
    };
  }

  if (typeKey === 'textarea') {
    return {
      key,
      label,
      type: 'textarea',
      required,
      placeholder: normString(raw.placeholder),
      maxLength: Number(raw.maxLength ?? DEFAULT_VIRAL_TEMPLATE_TEXTAREA_MAX_LENGTH),
    };
  }

  if (typeKey === 'imageupload') {
    const defaultUrl = coerceDefaultImageUrl(raw.default);

    return {
      key,
      label,
      type: 'imageUpload',
      required,
      category: pickNonEmptyString(raw.category),
      default: pickNonEmptyString(raw.value, defaultUrl),
    };
  }

  if (typeKey === 'imagedisplay') {
    const images = Array.isArray(raw.images) ? (raw.images as string[]) : [];
    return { key, label, type: 'imageDisplay', images };
  }

  return null;
}

/** 列表项 + 表单接口数据 → 详情弹窗结构 */
export function toTemplateDetailVO(
  tpl: { id: number; name: string; preview: string; cover: string },
  data: ViralTemplateFormData | undefined | null,
): TemplateDetailVO {
  const rawFields =
    (Array.isArray(data?.formSchema?.fields) ? data!.formSchema!.fields : null) ??
    (Array.isArray(data?.fields) ? data!.fields : null) ??
    [];

  const normalizedFields = rawFields
    .map((item) => normalizeTemplateFormField(item as Record<string, unknown>))
    .filter(Boolean) as TemplateFormField[];

  const consumedGroupedIndexes = new Set<number>();
  const fields: TemplateFormField[] = [];

  normalizedFields.forEach((field, index) => {
    if (consumedGroupedIndexes.has(index)) return;

    if (field.type !== 'imageUpload' || !field.category) {
      fields.push(field);
      return;
    }

    const groupedFields = [field];

    for (let i = index + 1; i < normalizedFields.length; i++) {
      const candidate = normalizedFields[i];
      if (
        candidate.type === 'imageUpload' &&
        candidate.category &&
        candidate.category === field.category
      ) {
        groupedFields.push(candidate);
        consumedGroupedIndexes.add(i);
      }
    }

    if (groupedFields.length === 1) {
      fields.push(field);
      return;
    }

    fields.push({
      key: field.key,
      label: field.label,
      type: 'groupedImageUpload',
      required: groupedFields.some((item) => item.required),
      category: field.category,
      slots: groupedFields.map((item) => ({
        key: item.key,
        required: item.required,
        default: item.default,
      })),
    });
  });

  return {
    templateId: data?.templateId ?? tpl.id,
    name: data?.name ?? tpl.name,
    preview: data?.preview ?? tpl.preview,
    cover: data?.cover ?? tpl.cover,
    formSchema: { fields },
  };
}

function buildInitialGroupedImageSlots(field: {
  slots: { default: string }[];
}): string[] {
  return field.slots.map((slot) => {
    const d = slot.default?.trim();
    return d || '';
  });
}

/** 从详情 schema 生成表单初始 formData */
export function buildInitialViralTemplateFormData(
  detail: TemplateDetailVO,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const f of detail.formSchema.fields) {
    if (f.type === 'imageDisplay') continue;
    if (f.type === 'groupedImageUpload') {
      out[f.key] = buildInitialGroupedImageSlots(f);
    } else if (f.type === 'imageUpload') {
      out[f.key] = f.default?.trim() || '';
    } else {
      out[f.key] = '';
    }
  }
  return out;
}

/** 校验必填字段是否已填写 */
export function validateViralTemplateFormData(
  detail: TemplateDetailVO,
  formData: Record<string, unknown>,
): { ok: true } | { ok: false; fieldKey: string } {
  for (const f of detail.formSchema.fields) {
    if (f.type === 'imageDisplay' || !f.required) continue;
    const v = formData[f.key];
    if (f.type === 'groupedImageUpload') {
      const arr = Array.isArray(v) ? (v as string[]) : [];
      for (let i = 0; i < f.slots.length; i++) {
        if (f.slots[i]?.required && !String(arr[i] ?? '').trim()) {
          return { ok: false, fieldKey: f.key };
        }
      }
    } else if (f.type === 'imageUpload') {
      if (!String(v ?? '').trim()) return { ok: false, fieldKey: f.key };
    } else if (!String(v ?? '').trim()) {
      return { ok: false, fieldKey: f.key };
    }
  }
  return { ok: true };
}

/**
 * 提交前按 schema 规范化，确保始终是完整的 key-value 结构
 */
export function normalizeViralTemplateSubmitFormData(
  detail: TemplateDetailVO,
  formData: Record<string, unknown>,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};

  for (const field of detail.formSchema.fields) {
    const raw = formData[field.key];

    if (field.type === 'imageDisplay') continue;

    if (field.type === 'groupedImageUpload') {
      const arr = Array.isArray(raw) ? raw : [];
      field.slots.forEach((slot, index) => {
        out[slot.key] = String(arr[index] ?? '').trim();
      });
      continue;
    }

    if (field.type === 'input' || field.type === 'textarea') {
      out[field.key] = normalizeTextSubmitValue(raw);
      continue;
    }

    out[field.key] = String(raw ?? '').trim();
  }

  return out;
}
